import { useState } from "react";
import {
  commitAllStagedChanges,
  discardAllStagedChanges,
  generateSourceCodeExport,
  getAllCurrentContent,
  getStagedSnapshot,
  EXPORTABLE_CONTENT_KEYS,
} from "@/hooks/useAdminStorage";
import { saveSiteContentToSupabase } from "@/hooks/useSiteContent";
import { useSiteContentContext } from "@/context/SiteContentContext";
import { useAdmin } from "@/context/AdminContext";
import {
  defaultHero,
  defaultWhatsIncluded,
  defaultSpacesSection,
  defaultApplication,
  defaultRules,
  defaultRulesCTA,
  defaultMission,
  defaultMeetMakers,
  defaultArtists,
  defaultFAQ,
  defaultContact,
  defaultFooter,
  defaultNavLinks,
  defaultNotifyBanner,
  defaultSectionVisibility,
  defaultSectionOrder,
  defaultLogoConfig,
  defaultWhyWeExist,
} from "@/data/siteContent";

const DEFAULT_SITE_META = {
  siteName: "Makers Alley Co",
  description: "A permanent, algorithm-free home for independent makers.",
  domain: "https://makersalley.co",
  ogImage: "",
  favicon: "/favicon.png",
  tags: "handmade artists, maker marketplace, year-round retail space, artist shelf rentals, sell handmade art, indie creators, artisan shop, Medford Oregon retail",
  googleVerification: "",
};

const DEFAULTS_MAP: Record<string, unknown> = {
  "ma_hero": defaultHero,
  "ma_whats_included": defaultWhatsIncluded,
  "ma_spaces_v2": defaultSpacesSection,
  "ma_application_v2": defaultApplication,
  "ma_rules": defaultRules,
  "ma_rules_cta": defaultRulesCTA,
  "ma_mission": defaultMission,
  "ma_meet_makers_header": defaultMeetMakers,
  "ma_artists_shared": defaultArtists,
  "ma_faq_v2": defaultFAQ,
  "ma_contact_v3": defaultContact,
  "ma_footer_v3": defaultFooter,
  "ma_nav_links": defaultNavLinks,
  "ma_notify_banner": defaultNotifyBanner,
  "ma_section_visibility": defaultSectionVisibility,
  "ma_section_order": defaultSectionOrder,
  "ma_section_data_map": {},
  "ma_section_labels": {},
  "ma_logo_font": "playfair",
  "ma_logo_config": defaultLogoConfig,
  "ma_gallery_deleted_builtins": [],
  "ma_site_notifications": [],
  "ma_custom_pages_v1": [],
  "ma_testimonials_v2": { pageVisible: false },
  "ma_leave_review_v3": { pageVisible: false },
  "ma_artists_meta": { pageVisible: false },
  "ma_submitted_reviews": [],
  "ma_site_meta": DEFAULT_SITE_META,
  "ma_admin_password": "makersalley2024",
  "ma_instagram_posts": [],
  "ma_why_we_exist": defaultWhyWeExist,
};

type SaveState = "idle" | "saving" | "saved" | "error";
type SyncState = "idle" | "syncing" | "synced" | "error";

export default function AdminSaveBar() {
  const { isAdmin, isMobilePreview, setIsMobilePreview, discardChanges, getCurrentPassword } = useAdmin();
  const { syncFromSupabase } = useSiteContentContext();

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string>("");
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [syncState, setSyncState] = useState<SyncState>("idle");
  const [syncMsg, setSyncMsg] = useState<string>("");
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportCode, setExportCode] = useState("");
  const [copied, setCopied] = useState(false);

  if (!isAdmin) return null;

  // ── Save to localStorage + Supabase ──────────────────────────────────────
  const handleSave = async () => {
    setSaveState("saving");
    setSaveError("");

    // 1. Capture staged snapshot BEFORE committing (commit clears the staging store)
    const stagedSnapshot = getStagedSnapshot();
    const dynamicKeys = Object.keys(stagedSnapshot).filter(
      (k) => !EXPORTABLE_CONTENT_KEYS.includes(k)
    );

    // 2. Commit to localStorage (writes staged values + clears staging store)
    commitAllStagedChanges();

    // 3. Build the full content map from localStorage for all known + dynamic keys
    const allKeys = [...EXPORTABLE_CONTENT_KEYS, ...dynamicKeys];
    const allContent = getAllCurrentContent(allKeys);
    const payloadKeys = Object.keys(allContent);

    // 4. Guard: if nothing to send, show error instead of fake success
    if (payloadKeys.length === 0) {
      setSaveError("Nothing to save — make edits first, then click Save");
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 5000);
      return;
    }

    console.log("[AdminSave] Sending payload with keys:", payloadKeys);

    const result = await saveSiteContentToSupabase(allContent, getCurrentPassword());
    console.log("[AdminSave] Save result:", result);
    if (!result.success) {
      setSaveError(`Saved locally. Supabase sync failed: ${result.error}`);
      setSaveState("error");
      setTimeout(() => setSaveState("idle"), 5000);
      return;
    }

    setSaveState("saved");
    setSaveError("");
    setSavedCount(result.count ?? null);
    console.log(`[AdminSave] Successfully synced ${result.count ?? "?"} keys to Supabase`);
    setTimeout(() => setSaveState("idle"), 2500);
  };

  // ── Sync from Supabase ────────────────────────────────────────────────────
  const handleSync = async () => {
    if (syncState === "syncing") return;
    setSyncState("syncing");
    setSyncMsg("");

    const result = await syncFromSupabase();

    if (result.success) {
      setSyncMsg(`Synced ${result.count} items — reload to see changes`);
      setSyncState("synced");
    } else {
      setSyncMsg(result.error || "Sync failed");
      setSyncState("error");
    }

    setTimeout(() => {
      setSyncState("idle");
      setSyncMsg("");
    }, 4000);
  };

  // ── Discard ───────────────────────────────────────────────────────────────
  const handleDiscard = () => {
    if (!discardConfirm) {
      setDiscardConfirm(true);
      return;
    }
    // Use the context discardChanges which reverts ALL React state
    // (section order, visibility, data map, logo, etc.) back to last-saved
    // AND clears the staging store. This properly undoes everything.
    discardChanges();
    setDiscardConfirm(false);
  };

  // ── Export to code ────────────────────────────────────────────────────────
  const handleExport = () => {
    const code = generateSourceCodeExport(DEFAULTS_MAP);
    setExportCode(code);
    setShowExport(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(exportCode).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Download .ts file ─────────────────────────────────────────────────────
  const handleDownload = () => {
    const allContent = getAllCurrentContent(EXPORTABLE_CONTENT_KEYS);
    const lines: string[] = [
      "// siteContent.ts — exported from admin mode",
      `// Generated: ${new Date().toISOString()}`,
      "// Drag this file into src/data/ to replace siteContent.ts",
      "",
      "// ─── EXPORTED CONTENT ───────────────────────────────────────────────────────",
      "",
    ];

    Object.entries(allContent).forEach(([key, value]) => {
      const varName = keyToVarName(key);
      lines.push(`export const ${varName} = ${serializeValue(value)};`);
      lines.push("");
    });

    const blob = new Blob([lines.join("\n")], { type: "text/typescript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "siteContent.exported.ts";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Download full JSON backup ─────────────────────────────────────────────
  const handleBackupDownload = () => {
    const allContent = getAllCurrentContent(EXPORTABLE_CONTENT_KEYS);
    const backup = {
      version: 1,
      exportedAt: new Date().toISOString(),
      content: allContent,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `makers-alley-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* ── Admin Save Bar ─────────────────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 w-full z-[200] flex items-center justify-center px-4 pb-4 pointer-events-none">
        <div className="pointer-events-auto flex flex-wrap items-center gap-2 sm:gap-3 bg-gray-900 text-white rounded-2xl px-4 py-2.5 border border-gray-700 max-w-4xl">

          {/* Label */}
          <span className="flex items-center gap-1.5 text-pink-400 text-sm font-semibold whitespace-nowrap">
            <i className="ri-edit-2-line"></i>
            <span className="hidden sm:inline">Admin</span>
          </span>

          {/* ── Save button ── */}
          {saveState === "saving" ? (
            <span className="flex items-center gap-1.5 text-yellow-400 text-sm font-semibold whitespace-nowrap">
              <i className="ri-loader-4-line animate-spin"></i>
              Saving...
            </span>
          ) : saveState === "saved" ? (
            <span className="flex items-center gap-1.5 text-green-400 text-sm font-semibold whitespace-nowrap" title={savedCount ? `Synced ${savedCount} items` : undefined}>
              <i className="ri-check-double-line"></i>
              {savedCount ? `Saved ${savedCount} items!` : "Saved!"}
            </span>
          ) : saveState === "error" ? (
            <span
              className="flex items-center gap-1.5 text-yellow-400 text-xs font-semibold whitespace-nowrap max-w-[200px] truncate cursor-help"
              title={saveError}
            >
              <i className="ri-alert-line"></i>
              {saveError.includes("Edge function not found")
                ? "Function not deployed — see admin panel"
                : saveError}
            </span>
          ) : (
            <button
              onClick={handleSave}
              className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-save-line mr-1"></i>
              Save
            </button>
          )}

          {/* ── Sync from Supabase ── */}
          <div className="border-l border-gray-600 pl-2 sm:pl-3 flex items-center gap-1.5">
            {syncState === "syncing" ? (
              <span className="flex items-center gap-1.5 text-sky-400 text-xs font-semibold whitespace-nowrap">
                <i className="ri-loader-4-line animate-spin"></i>
                Syncing...
              </span>
            ) : syncState === "synced" ? (
              <span className="flex items-center gap-1.5 text-sky-400 text-xs font-semibold whitespace-nowrap" title={syncMsg}>
                <i className="ri-check-line"></i>
                <span className="hidden sm:inline">{syncMsg}</span>
                <span className="sm:hidden">Synced!</span>
              </span>
            ) : syncState === "error" ? (
              <span className="flex items-center gap-1.5 text-red-400 text-xs font-semibold whitespace-nowrap" title={syncMsg}>
                <i className="ri-error-warning-line"></i>
                <span className="hidden sm:inline">Sync failed</span>
              </span>
            ) : (
              <button
                onClick={handleSync}
                className="text-xs text-sky-400 hover:text-sky-300 font-semibold cursor-pointer whitespace-nowrap transition-colors"
                title="Pull latest content from Supabase database into this browser"
              >
                <i className="ri-refresh-line mr-1"></i>
                <span className="hidden sm:inline">Sync from DB</span>
                <span className="sm:hidden">Sync</span>
              </button>
            )}
          </div>

          {/* ── Export to code ── */}
          <button
            onClick={handleExport}
            className="text-xs text-pink-400 hover:text-pink-300 font-semibold cursor-pointer whitespace-nowrap transition-colors border-l border-gray-600 pl-2 sm:pl-3"
            title="Export your edits as source code"
          >
            <i className="ri-code-line mr-1"></i>
            <span className="hidden sm:inline">Export</span>
          </button>

          {/* ── Download .ts file ── */}
          <button
            onClick={handleDownload}
            className="text-xs text-green-400 hover:text-green-300 font-semibold cursor-pointer whitespace-nowrap transition-colors"
            title="Download siteContent.ts with all current edits baked in"
          >
            <i className="ri-download-line mr-1"></i>
            <span className="hidden sm:inline">.ts</span>
          </button>

          {/* ── Backup JSON ── */}
          <button
            onClick={handleBackupDownload}
            className="text-xs text-amber-400 hover:text-amber-300 font-semibold cursor-pointer whitespace-nowrap transition-colors"
            title="Download full JSON backup of all site content"
          >
            <i className="ri-archive-line mr-1"></i>
            <span className="hidden sm:inline">Backup</span>
          </button>

          {/* ── Mobile Preview Toggle ── */}
          <button
            onClick={() => setIsMobilePreview(!isMobilePreview)}
            className={`text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors border-l border-gray-600 pl-2 sm:pl-3 flex items-center gap-1 ${
              isMobilePreview ? "text-sky-400" : "text-gray-400 hover:text-sky-400"
            }`}
            title={isMobilePreview ? "Exit mobile preview" : "Preview in mobile width"}
          >
            <i className="ri-smartphone-line"></i>
            <span className="hidden sm:inline">{isMobilePreview ? "Mobile On" : "Mobile"}</span>
          </button>

          {/* ── Discard ── */}
          {discardConfirm ? (
            <div className="flex items-center gap-2 border-l border-gray-600 pl-2 sm:pl-3">
              <span className="text-yellow-400 text-xs whitespace-nowrap">Discard?</span>
              <button
                onClick={handleDiscard}
                className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                Yes
              </button>
              <button
                onClick={() => setDiscardConfirm(false)}
                className="text-gray-400 hover:text-gray-200 text-xs cursor-pointer whitespace-nowrap"
              >
                No
              </button>
            </div>
          ) : (
            <button
              onClick={handleDiscard}
              className="text-gray-400 hover:text-red-400 text-sm cursor-pointer whitespace-nowrap transition-colors border-l border-gray-600 pl-2 sm:pl-3"
              title="Discard unsaved changes"
            >
              <i className="ri-close-line"></i>
            </button>
          )}
        </div>
      </div>

      {/* ── Export modal ──────────────────────────────────────────────────── */}
      {showExport && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowExport(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-3xl border p-6 bg-gray-900 border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-base">
                <i className="ri-code-line text-pink-400 mr-2"></i>
                Export Edits to Source Code
              </h3>
              <button
                onClick={() => setShowExport(false)}
                className="text-gray-400 hover:text-white cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="text-gray-400 text-xs mb-3">
              Copy this code and paste it into{" "}
              <code className="text-pink-400">src/data/siteContent.ts</code> to make your edits
              permanent. Replace the matching default exports with these updated values, then rebuild.
            </p>

            <div className="flex-1 overflow-auto">
              <pre className="bg-gray-950 border border-gray-800 rounded-xl p-4 text-xs text-green-300 font-mono whitespace-pre-wrap break-all max-h-[45vh] overflow-auto">
                {exportCode}
              </pre>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleCopy}
                className="flex-1 bg-pink-500 hover:bg-pink-600 text-white text-sm font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className={`${copied ? "ri-check-line" : "ri-clipboard-line"} mr-1`}></i>
                {copied ? "Copied!" : "Copy to Clipboard"}
              </button>
              <button
                onClick={handleDownload}
                className="px-5 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-download-line mr-1"></i>
                Download .ts
              </button>
              <button
                onClick={() => setShowExport(false)}
                className="px-6 border border-gray-600 text-gray-300 text-sm font-semibold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors hover:border-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function keyToVarName(key: string): string {
  const map: Record<string, string> = {
    "ma_hero": "defaultHero",
    "ma_whats_included": "defaultWhatsIncluded",
    "ma_spaces_v2": "defaultSpacesSection",
    "ma_application_v2": "defaultApplication",
    "ma_rules": "defaultRules",
    "ma_rules_cta": "defaultRulesCTA",
    "ma_mission": "defaultMission",
    "ma_meet_makers_header": "defaultMeetMakers",
    "ma_artists_shared": "defaultArtists",
    "ma_faq_v2": "defaultFAQ",
    "ma_contact_v3": "defaultContact",
    "ma_footer_v3": "defaultFooter",
    "ma_nav_links": "defaultNavLinks",
    "ma_notify_banner": "defaultNotifyBanner",
    "ma_section_visibility": "defaultSectionVisibility",
    "ma_section_order": "defaultSectionOrder",
    "ma_section_data_map": "defaultSectionDataMap",
    "ma_section_labels": "defaultSectionLabels",
    "ma_logo_font": "defaultLogoFont",
    "ma_logo_config": "defaultLogoConfig",
    "ma_gallery_deleted_builtins": "defaultDeletedBuiltins",
    "ma_site_notifications": "defaultNotifications",
    "ma_custom_pages_v1": "defaultCustomPages",
    "ma_testimonials_v2": "defaultTestimonialsPage",
    "ma_leave_review_v3": "defaultLeaveReviewPage",
    "ma_artists_meta": "defaultArtistsMeta",
    "ma_submitted_reviews": "defaultSubmittedReviews",
    "ma_why_we_exist": "defaultWhyWeExist",
  };
  return map[key] || key;
}

function serializeValue(value: unknown): string {
  return JSON.stringify(value, null, 2)
    .replace(/"([^"]+)":/g, "$1:")
    .replace(/"/g, "'");
}
